import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import EntityMember from "_shared/entity-members/entity-member";
import TeamRosterTable, {
  TEAM_LEADER_ROLE,
  TEAM_MEMBER_ROLE,
  TEAM_ROSTER_PERSON_COLUMNS,
  teamRosterCell,
  type TeamRosterCell,
  type TeamRosterRow,
} from "_shared/entity-members/_views/team-roster-table";
import Guid from "_shared/guids/guid";
import Loader from "_shared/_views/loading/loader";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useSession } from "users/__session/session-context";
import Proposal from "work/proposals/proposal";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import RowLink from "_shared/_views/tables/row-link";
import { MaskedTeam } from "work/masked-fields";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ClientTeamTemplatePanel from "work/proposals/_views/_shared/client-team-template-panel";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";
import {
  paneIsCompact,
  workPaneNarrowerThan,
  workPaneProseInset,
} from "work/_views/work-pane-layout";

const ClientTeamViewContainer = styled(Box)(() => ({
  display: "flex",
  flex: 1,
  height: "100%",
  minHeight: 0,
  position: "relative",
  width: "100%",
}));
/**
 * The tab's two columns: the templates panel and the roster beside it, in the Conflicts and Vendor
 * Team arrangement. Stacked rather than side by side once the PANE — not the window — is too narrow
 * to hold both, which is the same question `ResizableTemplatePanel` asks.
 */
const ClientTeamLayout = styled(Grid)(({ theme }) => ({
  height: "100%",
  minHeight: "20rem",
  minWidth: 0,
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    minHeight: 0,
  },
}));
const RosterColumn = styled(Grid)(() => ({
  display: "flex",
  minWidth: 0,
}));
/**
 * The roster, with its heading strip inset and its table left flush.
 *
 * "Team Roster" is prose — see `workPaneProseInset` — and with the tab's row running flush to the
 * pane it had nothing holding it off the resize divider. The table below it keeps meeting the
 * divider, which is what the column was asked for. `data-roster-header` is the hook
 * `TeamRosterSection` documents for addressing the strip without an Emotion class name.
 */
const Roster = styled(TeamRosterTable)(({ theme }) => ({
  "& [data-roster-header]": workPaneProseInset(theme),
}));
/**
 * No left gutter, for the same reason the Vendor Team tab's roster has none: the resize handle beside
 * it is already the boundary, and a gutter as well indents the roster inside its own column. The
 * vertical padding stays — that is room at the bottom of the tab, not a division between panels.
 */
const ClientTeamContainer = styled(Box)(({ theme }) => ({
  columnGap: theme.spacing(10),
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  paddingBottom: theme.spacing(3),
  rowGap: theme.spacing(3),
  width: "100%",
  [workPaneNarrowerThan(theme.breakpoints.values.lg)]: {
    columnGap: theme.spacing(3),
  },
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    paddingBottom: 0,
  },
}));
const StatusMessage = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(3),
}));
/**
 * The roster and whatever the tab has to say above it. Drawn only when there is something to say,
 * so a tab with no unsaved roster edit keeps exactly the layout it had.
 */
const RosterStack = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  rowGap: theme.spacing(1),
  width: "100%",
}));
const ClientTeamLoader = styled(Loader)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(3),
  top: theme.spacing(2),
}));
const memberSelectionPanelWidth = "min(500px, 100vw)";
const MemberSelectionDrawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    height: "calc(100% - 64px)",
    maxWidth: "100vw",
    overflow: "hidden",
    top: "64px",
    width: memberSelectionPanelWidth,
    // The paper is positioned against the window, not against the pane, so how far down the app bar
    // ends is a question about the window — one of the few on these pages that still is.
    [theme.breakpoints.down("lg")]: {
      height: `calc(100% - ${theme.spacing(6)})`,
      top: theme.spacing(6),
    },
  },
}));
const MemberSelectionPanel = styled(MemberSelectionDrawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition",
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    width: open ? memberSelectionPanelWidth : 0,
  },
}));
const MemberSelectionPanelContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  maxWidth: "100vw",
  minHeight: 0,
  width: memberSelectionPanelWidth,
}));
const MemberSelectionPanelTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2, 1),
}));
const MemberSelectionPanelContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));
const MemberSelectionPanelActions = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  padding: theme.spacing(1.5, 2, 10),
}));
const MemberSelectionCancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

/**
 * What a roster row says when the directory will not name the person on it. A membership is two
 * GUIDs, and a GUID is not something to show a reader: an unresolved row is described, not
 * identified (issue #4830).
 */
const UNNAMED_CLIENT_TEAM_MEMBER = "Client team member";
/** Room for the one control a client roster row carries: remove. */
const CLIENT_ROSTER_ACTIONS_COLUMN_WIDTH = 90;

type ProposalClientTeamViewProps = {
  proposal?: Proposal;
  clientTeam?: ClientTeam | MaskedTeam | null;
  negotiationId?: string;
  canEdit?: boolean;
  /**
   * Whether the tab draws the templates column beside the roster.
   *
   * Off by default, so the surfaces that only ever read this roster — the hired work agreement and
   * the archived proposal — keep the single-column page they have. A panel offering templates to
   * apply on a page where nothing may be applied would be furniture.
   */
  showTemplates?: boolean;
  /**
   * True where the side pane has the room, so the templates column gives up its width for a rail.
   *
   * The seam is here, but no page passes it yet: the proposal page shells are contested by other work
   * in flight and this is not worth a line in one of them. Nothing is lost meanwhile — the panel
   * already collapses itself when the tab's PANE gets narrow, which is what opening the comments pane
   * does, so the rail is an affordance rather than the mechanism.
   */
  isDrawerOpen?: boolean;
  /**
   * The roster to draw, where the page is staging it for its own Save (#5116).
   *
   * Its presence is what tells this component the roster is held elsewhere: it then owns no copy
   * of the member list and simply reports every edit through {@link onMembersChanged}. Absent on
   * the surfaces with nothing to stage against — an uncreated draft, whose members go into the
   * `ProposalBuilder`, and the read-only rosters.
   */
  members?: readonly EntityMember[];
  /** Whether {@link members} carries an edit the page has not written yet. */
  hasUnsavedChanges?: boolean;
  /** Whether somebody else's roster change landed underneath that unsaved edit. */
  changedElsewhereWhileEditing?: boolean;
  onMembersChanged?: (members: EntityMember[]) => void;
};

export default function ProposalClientTeamView(props: Readonly<ProposalClientTeamViewProps>) {
  const { proposal, negotiationId, onMembersChanged } = props;
  // `clientTeam` is nullable and callers pass an explicit `null`, so only `undefined` falls back.
  const clientTeam = props.clientTeam === undefined ? proposal?.clientTeam : props.clientTeam;
  const canEdit = props.canEdit ?? false;
  const showTemplates = props.showTemplates ?? false;
  const isDrawerOpen = props.isDrawerOpen ?? false;

  const visibleClientTeam = MaskedTeam.isPresentNotMasked(clientTeam) ? clientTeam : undefined;
  const teamMemberKey = visibleClientTeam
    ? [
      proposal?.id?.value,
      visibleClientTeam.leader?.userId.value,
      visibleClientTeam.leader?.legalEntityId.value,
      ...visibleClientTeam.members.map((member) => `${member.userId.value}:${member.legalEntityId.value}`),
    ].join("|")
    : proposal?.id?.value;

  return (
    <ProposalClientTeamRoster
      key={teamMemberKey}
      proposal={proposal}
      clientTeam={clientTeam}
      negotiationId={negotiationId}
      canEdit={canEdit}
      showTemplates={showTemplates}
      isDrawerOpen={isDrawerOpen}
      members={props.members}
      hasUnsavedChanges={props.hasUnsavedChanges}
      changedElsewhereWhileEditing={props.changedElsewhereWhileEditing}
      onMembersChanged={onMembersChanged}
    />
  );
}

function ProposalClientTeamRoster(props: Readonly<ProposalClientTeamViewProps>) {
  const { proposal, negotiationId, onMembersChanged } = props;
  // `clientTeam` is nullable and callers pass an explicit `null`, so only `undefined` falls back.
  const clientTeam = props.clientTeam === undefined ? proposal?.clientTeam : props.clientTeam;
  const canEdit = props.canEdit ?? false;
  const showTemplates = props.showTemplates ?? false;
  const isDrawerOpen = props.isDrawerOpen ?? false;

  const paneIsNarrow = useWorkPaneIsCompact();
  const { openDialog } = useDialog();
  const session = useSession();
  const stagedMembers = props.members;
  const visibleClientTeam = MaskedTeam.isPresentNotMasked(clientTeam) ? clientTeam : undefined;
  const leader = visibleClientTeam?.leader;
  const savedMembers: EntityMember[] = visibleClientTeam?.members.filter(
    (member) => !leader || !member.userId.isEqualTo(leader.userId),
  ) ?? [];
  // Held here only where nobody else is holding it. A page that stages the roster for its Save
  // passes it in, and this component then owns no copy that could disagree with it (#5116).
  const [localMembers, setLocalMembers] = useState<EntityMember[]>(() => savedMembers.map((member) => member.clone()));
  const members: readonly EntityMember[] = stagedMembers ?? localMembers;
  const [memberSelectionOpen, setMemberSelectionOpen] = useState(false);

  const teamMembers = useMemo(
    () => [leader, ...members].filter(Boolean) as EntityMember[],
    [leader, members],
  );
  const userIds = useMemo(() => new Set(teamMembers.map((member) => member.userId.value)), [teamMembers]);
  const individuals = useSelector(getIndividualsByUserIds(userIds));
  const missingIds = useMemo(() => {
    // `individuals` is one row per userId (see individuals-redux-slice.ts's `byUserId` cache), so
    // hydration is keyed by userId alone. Name/avatar/email are personal attributes, not scoped to
    // a legal entity, and keying on the compound `userId:legalEntityId` pair only causes misses
    // when the cached row's legalEntityId doesn't match the roster membership's.
    const loaded = new Set(individuals.map((individual) => individual.userId.value));
    return new Set(
      teamMembers
        .filter((member) => !loaded.has(member.userId.value))
        .map((member) => member.userId.value),
    );
  }, [individuals, teamMembers]);
  const individualsQuery = useGetIndividualsByUserIdsQuery(
    missingIds.size > 0 ? { userIds: missingIds } : skipToken,
  );
  const individualsByUserId = useMemo(
    () => new Map(individuals.map((individual) => [individual.userId.value, individual])),
    [individuals],
  );

  // An uncreated draft has no negotiation to stage a roster against, so its members go straight
  // into the page's `ProposalBuilder`; a created one is staged by the page for its Save. Both wait
  // for an explicit press, which is why neither writes from here (#5116).
  const canManageLocalRoster = Boolean(canEdit && onMembersChanged && !negotiationId);
  const canManagePersistedRoster = Boolean(canEdit && negotiationId && leader &&
    leader.userId.isEqualTo(session.user?.id) &&
    leader.legalEntityId.isEqualTo(session.user?.legalEntityId));
  const canManage = Boolean(leader && (canManageLocalRoster || canManagePersistedRoster));

  /**
   * Records the roster the viewer means the client team to have. It writes nothing.
   *
   * Client team membership is still reference state carried by its own command rather than a
   * content operation — only *when* that command is sent has changed. The roster now waits for the
   * page's Save like every other tab's edits, so that a client team leader who adds the wrong
   * person, or removes the right one by accident, is not already committed to it (#5116).
   */
  const updateMembers = useCallback((nextMembers: EntityMember[]) => {
    if (!stagedMembers) setLocalMembers(nextMembers);
    onMembersChanged?.(nextMembers.map((member) => member.clone()));
  }, [onMembersChanged, stagedMembers]);

  const addMember = useCallback(
    (individual: { toEntityMember: () => EntityMember; fullName?: string }) => {
      const member = individual.toEntityMember();
      if (!member.userId || !member.legalEntityId || member.userId.isEqualTo(leader?.userId)) return;
      if (members.some((existing) => existing.isEqualTo(member))) return;
      updateMembers([...members, member]);
    },
    [leader?.userId, members, updateMembers],
  );

  /**
   * Takes a member off the roster the page is holding.
   *
   * No confirmation. Removing somebody used to revoke their access the instant the row's button was
   * pressed, which is what the dialog was guarding; the removal is now staged and reversible until
   * Save, and the notice above the roster names the consequence the press no longer has.
   */
  const removeMember = useCallback((member: EntityMember) => {
    updateMembers(members.filter((item) => !item.isEqualTo(member)));
  }, [members, updateMembers]);

  /**
   * What the templates panel asks for: the roster the checkbox it just took means.
   *
   * Straight into {@link updateMembers}, which is the roster's one path. A template applying
   * several people at once is one staged roster, not one per person.
   */
  const applyTemplateMembers = useCallback(
    (nextMembers: EntityMember[]) => {
      if (!canManage) return;
      updateMembers(nextMembers);
    },
    [canManage, updateMembers],
  );

  const openMemberSelector = useCallback(() => setMemberSelectionOpen(true), []);
  const closeMemberSelector = useCallback(() => setMemberSelectionOpen(false), []);
  const openUserProfileDialog = useCallback((individualId: Guid) => {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }, [openDialog]);

  /**
   * One roster row, drawn entirely from the resolved {@link Individual} rather than from the
   * membership: the membership is two GUIDs, and a GUID identifies nobody to a reader (#4830).
   *
   * `onRemove` is the row's only control. A row without one still renders — a viewer who may not
   * manage the roster reads the very same people.
   */
  function rosterRow(member: EntityMember, role: string, onRemove?: () => void): TeamRosterRow {
    const individual = individualsByUserId.get(member.userId.value);
    return {
      id: `${member.userId.value}:${member.legalEntityId.value}`,
      searchText: rosterSearchText(individual),
      // The search string appends the secondary line to the name; the Name column is ordered by the
      // name the row actually draws (#5164).
      sortText: individual?.fullName ?? UNNAMED_CLIENT_TEAM_MEMBER,
      // No initials to draw without a name, and the first two characters of a GUID are not initials
      // -- they are the identifier leaking into the page. An empty avatar keeps the row aligned and
      // says nothing false.
      avatar: individual ? <IndividualAvatar individual={individual} /> : <Avatar />,
      name: memberNameCell(individual, openUserProfileDialog),
      email: memberEmailCell(individual),
      company: teamRosterCell(individual?.entityName),
      role: { content: role },
      actions: onRemove ? (
        <ProposalDeleteActionButton aria-label="Remove client team member" onClick={onRemove} />
      ) : undefined,
    };
  }

  if (clientTeam && !visibleClientTeam) {
    return (
      <ClientTeamViewContainer>
        <StatusMessage color="text.secondary">Client team details are unavailable for this viewer.</StatusMessage>
      </ClientTeamViewContainer>
    );
  }
  if (!clientTeam || !leader) {
    return (
      <ClientTeamViewContainer>
        <StatusMessage color="text.secondary">A Client Representative is required before the Client Team can add members.</StatusMessage>
      </ClientTeamViewContainer>
    );
  }

  const rosterRows = [
    rosterRow(leader, TEAM_LEADER_ROLE),
    ...members.map((member) =>
      rosterRow(
        member,
        TEAM_MEMBER_ROLE,
        canManage ? () => removeMember(member) : undefined,
      ),
    ),
  ];

  /**
   * The templates column, drawn once. It is offered only where the roster can actually take one:
   * a viewer who may not manage this roster is shown the table alone rather than a panel whose every
   * checkbox would refuse.
   */
  // Takes the leader rather than closing over it: the guards above narrow `leader` to an
  // EntityMember for the rest of the body, but that narrowing does not reach inside a nested
  // function, and a template panel without a Client Representative is not a thing this tab has.
  function renderTemplatePanel(panelLeader: EntityMember) {
    return (
      <ClientTeamTemplatePanel
        leader={panelLeader}
        members={members}
        busy={false}
        compact={paneIsNarrow}
        canCreateTemplate={canManagePersistedRoster}
        onMembersChanged={applyTemplateMembers}
      />
    );
  }

  const showTemplatePanel = showTemplates && canManage;
  const stagedUserIds = new Set(members.map((member) => member.userId.value));
  const unsavedNotice = clientTeamRosterUnsavedNotice({
    hasUnsavedChanges: Boolean(props.hasUnsavedChanges),
    changedElsewhereWhileEditing: Boolean(props.changedElsewhereWhileEditing),
    removedCount: savedMembers.filter((member) => !stagedUserIds.has(member.userId.value)).length,
  });
  /* One table, the way every team tab reads since #4955: the Client Representative leads it and the
     members follow, each row saying which it is in its Role cell. The representative is still a
     single scalar the proposal is built around and the members still a collection keyed by the
     compound `userId:legalEntityId` — what says so is the row's own controls, which only the client
     leader is offered at all. */
  const rosterTable = (
    <Roster
      title="Team Roster"
      surface="client-team"
      columns={TEAM_ROSTER_PERSON_COLUMNS}
      rows={rosterRows}
      emptyMessage="No Team Members"
      noMatchesMessage="No matching team members"
      // Offered on every surface this roster serves, including the archived proposal and the
      // hired work agreement. Unlike the vendor tab — three separate components, one of whose
      // whole claim is that it offers nothing to do — this is one roster the client leader
      // grows a member at a time, and a reader of a long one has the same reason to find a
      // person on it whether or not they may still change it.
      enableSearch
      // A row here carries one control — remove. The default is sized for the active
      // proposal's five, which would park an empty pinned column over the Role cell.
      actionsColumnWidth={CLIENT_ROSTER_ACTIONS_COLUMN_WIDTH}
      addActions={canManage ? [{ label: "Add Member", onClick: openMemberSelector }] : undefined}
    />
  );

  return (
    <ClientTeamViewContainer>
      {(individualsQuery.isLoading || individualsQuery.isFetching) && <ClientTeamLoader hideText />}
      <ClientTeamLayout container>
        {showTemplatePanel && (
          <>
            {paneIsNarrow ? (
              renderTemplatePanel(leader)
            ) : (
              // No `selectedCount`: how many templates read as applied is derived inside the panel
              // from the roster, and a rail badge fed by a copy of it kept out here is a second
              // answer that can disagree with the first. A badge of nothing claims nothing.
              <ResizableTemplatePanel minimized={isDrawerOpen}>{renderTemplatePanel(leader)}</ResizableTemplatePanel>
            )}
            {!paneIsNarrow && (
              <Grid>
                <Divider orientation="vertical" />
              </Grid>
            )}
          </>
        )}
        <RosterColumn size="grow">
          <ClientTeamContainer>
            {unsavedNotice ? (
              <RosterStack>
                <Alert severity="info">{unsavedNotice}</Alert>
                {rosterTable}
              </RosterStack>
            ) : (
              rosterTable
            )}
          </ClientTeamContainer>
        </RosterColumn>
      </ClientTeamLayout>
      <MemberSelectionPanel open={memberSelectionOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
        <MemberSelectionPanelContainer>
          <MemberSelectionPanelTitleBar>
            <Typography variant="h5">Add client team member</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={closeMemberSelector}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </MemberSelectionPanelTitleBar>
          <MemberSelectionPanelContent>
            <VendorTeamMemberSelection
              selectLeader={false}
              selectMember
              initialActiveTab="coworkers"
              selectedTeamMemberUserIds={[leader?.userId, ...members.map((member) => member.userId)].filter(Boolean) as Guid[]}
              onNewSelection={addMember}
              closeDialog={closeMemberSelector}
            />
          </MemberSelectionPanelContent>
          <MemberSelectionPanelActions>
            <MemberSelectionCancelButton fullWidth variant="outlined" color="primary" onClick={closeMemberSelector}>
              Cancel
            </MemberSelectionCancelButton>
          </MemberSelectionPanelActions>
        </MemberSelectionPanelContainer>
      </MemberSelectionPanel>
    </ClientTeamViewContainer>
  );
}

/**
 * A roster row's name line.
 *
 * A name the API sent because the viewer is on this proposal, whose marketplace profile is not
 * published, is a name with no profile behind it — so it is shown rather than offered as a link to a
 * dialog that would come back with the name again (#4840). A row the directory will not name at all
 * is described instead (#4830).
 */
function memberNameCell(individual: Individual | undefined, onViewProfile: (individualId: Guid) => void) {
  if (!individual) return <Typography>{UNNAMED_CLIENT_TEAM_MEMBER}</Typography>;
  if (individual.nameOnly) return <Typography>{individual.fullName}</Typography>;
  // `RowLink` rather than this page's own info link: #5122 made it the one way a table row says it
  // opens something, underlined at rest and inheriting the cell's own type. Drawn as an `info.main`
  // `body1` instead, this one name read as a different kind of thing from every other clickable name
  // in the app. A button, because the profile opens in a dialog with no URL to point at.
  return (
    <RowLink ariaLabel={individual.fullName} onClick={() => onViewProfile(individual.id)}>
      {individual.fullName}
    </RowLink>
  );
}

/**
 * What the roster's Email column says about a row.
 *
 * A published profile offers its email. A name-only individual — somebody the viewer is told about
 * because they share this proposal, whose marketplace profile is unpublished — has no email to
 * offer, and its absence is not something to explain, so the column shows its own empty mark. A member
 * the directory would not name at all gets a description in its place: the row used to print the raw
 * legal-entity GUID here, which tells the reader nothing and reads as a defect (issue #4830).
 */
function memberSecondaryText(individual?: Individual): string | undefined {
  if (individual) return individual.email?.value;
  return "Profile unavailable";
}

function memberEmailCell(individual?: Individual): TeamRosterCell {
  return teamRosterCell(memberSecondaryText(individual));
}

/**
 * Everything the roster's search should match a row on — what the row shows, and nothing it does
 * not. A row the directory will not name is searchable by the words it actually carries, never by
 * the identifiers it deliberately withholds.
 *
 * A search key only; the row's `sortText` is what the Name column is ordered by (#5164).
 */
function rosterSearchText(individual?: Individual): string {
  const name = individual?.fullName ?? UNNAMED_CLIENT_TEAM_MEMBER;
  const secondary = memberSecondaryText(individual) ?? "";
  return `${name} ${secondary}`.trim();
}

/**
 * What the tab says above the roster about an edit the page has not written yet, or nothing when
 * there is nothing to say.
 *
 * Two facts, in the order they matter. The first is that the roster in front of the reader is not
 * the one the proposal has — the change they can see is waiting for Save, and a removal among it
 * is where their teammate's access actually goes. The second is that somebody else moved the
 * roster underneath the edit: that is a warning rather than a failure, because the staged change
 * is re-expressed against the newer roster rather than replacing it (see
 * `rebaseProposalClientTeamRoster`), so nobody's membership is quietly undone by pressing Save.
 */
export function clientTeamRosterUnsavedNotice(args: {
  hasUnsavedChanges: boolean;
  changedElsewhereWhileEditing: boolean;
  removedCount: number;
}): string | undefined {
  if (!args.hasUnsavedChanges) return undefined;
  const removal =
    args.removedCount > 0
      ? ` Saving removes ${args.removedCount === 1 ? "that member's" : "those members'"} access to this proposal.`
      : "";
  const elsewhere = args.changedElsewhereWhileEditing
    ? " The client team also changed elsewhere while you were editing; your changes have been kept on top of the latest roster."
    : "";
  return `Unsaved client team changes. Save the proposal to apply them.${removal}${elsewhere}`;
}
